import React from "react";
import { OrbitControls } from "@react-three/drei";
import { Texture } from "three";
import { InstallationType } from "../../../../../../core/common_types";
import { WindowSize, GridSize } from "../../../../../../models/door_configuration";
import { OverbasStep, IntegratedDoorPosition, getPosizionePorta } from "./constants";
import { GarageWalls } from "./garage_walls";
import { MuroConSquarcio } from "./muro_con_squarcio";
import { PannelliAnimati } from "./pannelli_animati";
import { PortinaLaterale } from "./portina_laterale";
import { TelaioPerimetrale } from "./telaio_perimetrale";
import { Tubolari, TubolariGeometria, getTubolariLayout } from "./tubolari";

export type SceneProps = {
  larghezza: number;
  altezza: number;
  spessore: number;
  isOpen: boolean;
  showPortina: boolean;
  portinaPosizione: IntegratedDoorPosition;
  portinaLarghezza: number;
  portinaColore: Texture | undefined;
  portinaColoreInterno: Texture | undefined;
  portinaManigliaCOlore: string;
  portinaManigliaCOloreInterno: string;
  portinaSogliaAltezza: number;
  portinaApertura: "sx" | "dx";
  portinaLateraleApertura: "sx" | "dx";
  showPortinaLaterale: boolean;
  portinaLateraleInstallazione: InstallationType;
  portinaLateraleCorniceColorCode: Texture | undefined;
  portinaLateralePannelloColorCode: Texture | undefined;
  portinaLateraleHandleColor: string;
  portinaLateralePanelCount: number;
  vanoConAltroAccesso: boolean;
  step: OverbasStep;
  profondita: number;
  larghezzaForo: number;
  altezzaForo: number;
  oltreLuceSx: boolean;
  oltreLuceDx: boolean;
  oltreLuceTraversa: boolean;
  larghezzaSpallettaSx: number;
  larghezzaSpallettaDx: number;
  altezzaArchitrave: number;
  finestreAttive: Set<string>;
  griglieAttive: Set<string>;
  onFinestraClick: (panelIndex: number, finestraIndex: number) => void;
  colorePannello: Texture | undefined;
  coloreInternoPannello: Texture | undefined;
  corniceFinestratura: Texture | undefined;
  isWide: boolean;
  isFullVision: boolean;
  isTwoPanels: boolean;
  pietraveraColumns: number;
  windowSize: WindowSize;
  hoverColor: string;
  gridSize: GridSize;
  grigliaColor: string | undefined;
  tipoManiglia?: string | null;
  coloreManiglia?: string;
  rawAltezza?: number;
  isDebordante?: boolean;
  coloreBordoTubolare: string;
  coloreTelaioPerimetrale?: string;
};

export const Scene: React.FC<SceneProps> = ({
  larghezza,
  altezza,
  spessore,
  isOpen,
  showPortina,
  portinaPosizione,
  portinaLarghezza,
  portinaColore,
  portinaColoreInterno,
  portinaManigliaCOlore,
  portinaManigliaCOloreInterno,
  portinaSogliaAltezza,
  portinaApertura,
  portinaLateraleApertura,
  showPortinaLaterale,
  portinaLateraleInstallazione,
  portinaLateraleCorniceColorCode,
  portinaLateralePannelloColorCode,
  portinaLateraleHandleColor,
  portinaLateralePanelCount,
  vanoConAltroAccesso,
  step,
  profondita,
  larghezzaForo,
  altezzaForo,
  oltreLuceSx,
  oltreLuceDx,
  oltreLuceTraversa,
  larghezzaSpallettaSx,
  larghezzaSpallettaDx,
  altezzaArchitrave,
  finestreAttive,
  griglieAttive,
  onFinestraClick,
  colorePannello,
  coloreInternoPannello,
  corniceFinestratura,
  isWide,
  isFullVision,
  isTwoPanels,
  pietraveraColumns,
  windowSize,
  hoverColor,
  gridSize,
  grigliaColor,
  tipoManiglia,
  coloreManiglia,
  rawAltezza,
  isDebordante,
  coloreBordoTubolare,
  coloreTelaioPerimetrale,
}) => {
  // La porta e' larga/alta quanto ordinato: e' la sua posizione rispetto al foro
  // a cambiare fra installazione in luce (dentro il foro) e oltre luce (sul muro).
  // I selettori del form sono riferiti alla vista interna ("guida destra (vista
  // interna)"): in vista esterna il montante dx interno e' quello di sinistra a
  // schermo, come gia' fa il render 2D (vedi PortaOverlap).
  const oltreLuceSinistra = oltreLuceDx;
  const oltreLuceDestra   = oltreLuceSx;

  const posizionePorta = getPosizionePorta(larghezzaForo, larghezza, spessore, {
    sx: oltreLuceSinistra,
    dx: oltreLuceDestra,
    traversa: oltreLuceTraversa,
  });

  // Stessi dati per i tubolari e per lo squarcio nel muro: il controtelaio in luce
  // ha sezione fissa e sborda oltre il foro quando lo sguincio e' piu' stretto,
  // quindi il muro va scavato fino al suo ingombro per non nasconderlo.
  const tubolariGeometria: TubolariGeometria = {
    xPorta: posizionePorta.x,
    zPorta: posizionePorta.z,
    larghezzaPorta: larghezza,
    altezzaPorta: altezza,
    spessorePorta: spessore,
    larghezzaForo,
    altezzaForo,
    inLuceSinistra: !oltreLuceSinistra,
    inLuceDestra: !oltreLuceDestra,
    inLuceTraversa: !oltreLuceTraversa,
  };

  // Margine reale fra foro e parete laterale/soffitto: la spalletta/architrave
  // reale, mai piu' stretta pero' dell'ingombro dei tubolari (altrimenti il
  // controtelaio in luce resterebbe annegato nella muratura).
  const ingombroTubolari = getTubolariLayout(tubolariGeometria).ingombro;
  const margineSx  = Math.max(larghezzaSpallettaSx, -ingombroTubolari.xMin, 0);
  const margineDx  = Math.max(larghezzaSpallettaDx, ingombroTubolari.xMax - larghezzaForo, 0);
  const margineTop = Math.max(altezzaArchitrave, ingombroTubolari.yMax - altezzaForo, 0);

  return (
    <>
      <ambientLight intensity={2} />
      <directionalLight position={[5, 8, 5]} intensity={1.2} castShadow />
      <pointLight position={[larghezzaForo / 2, altezzaForo * 0.8, -profondita / 2]} intensity={1.5} decay={1} />

      <GarageWalls
        larghezza={larghezzaForo}
        altezza={altezzaForo}
        profondita={profondita}
        showPortinaLaterale={showPortinaLaterale}
        vanoConAltroAccesso={vanoConAltroAccesso}
        margineSx={margineSx}
        margineDx={margineDx}
        margineTop={margineTop}
      />
      <MuroConSquarcio
        larghezza={larghezzaForo}
        altezza={altezzaForo}
        showPortinaLaterale={showPortinaLaterale}
        squarcio={ingombroTubolari}
        margineSx={margineSx}
        margineDx={margineDx}
        margineTop={margineTop}
      />

      <Tubolari {...tubolariGeometria} colore={colorePannello} bordoColore={coloreBordoTubolare} />

      <group position={[posizionePorta.x, 0, posizionePorta.z]}>
        <TelaioPerimetrale larghezza={larghezza} altezza={altezza} spessore={spessore} colore={coloreTelaioPerimetrale} />

        <PannelliAnimati
          larghezza={larghezza}
          altezza={altezza}
          spessore={spessore}
          isOpen={isOpen}
          showPortina={showPortina}
          portinaPosizione={portinaPosizione}
          portinaLarghezza={portinaLarghezza}
          portinaColore={portinaColore}
          portinaColoreInterno={portinaColoreInterno}
          portinaManigliaCOlore={portinaManigliaCOlore}
          portinaManigliaCOloreInterno={portinaManigliaCOloreInterno}
          portinaSogliaAltezza={portinaSogliaAltezza}
          portinaApertura={portinaApertura}
          step={step}
          finestreAttive={finestreAttive}
          griglieAttive={griglieAttive}
          onFinestraClick={onFinestraClick}
          colorePannello={colorePannello}
          coloreInternoPannello={coloreInternoPannello}
          corniceFinestratura={corniceFinestratura}
          isWide={isWide}
          isFullVision={isFullVision}
          isTwoPanels={isTwoPanels}
          pietraveraColumns={pietraveraColumns}
          windowSize={windowSize}
          hoverColor={hoverColor}
          gridSize={gridSize}
          grigliaColor={grigliaColor}
          tipoManiglia={tipoManiglia}
          coloreManiglia={coloreManiglia}
          rawAltezza={rawAltezza}
          isDebordante={isDebordante}
        />
      </group>

      {showPortinaLaterale && (
        <PortinaLaterale
          installazione={portinaLateraleInstallazione}
          larghezza={larghezzaForo}
          corniceColorCode={portinaLateraleCorniceColorCode}
          pannelloColorCode={portinaLateralePannelloColorCode}
          panelCount={portinaLateralePanelCount}
          apertura={portinaLateraleApertura}
          handleColor={portinaLateraleHandleColor}
        />
      )}

      <OrbitControls makeDefault target={[larghezzaForo / 2, altezzaForo / 2, 0]} />
      <axesHelper args={[1]} />
    </>
  );
};
